import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type HealthSummary } from './api.ts';
import type { StatusData } from '../components/layout/StatusBar.tsx';

export type AsyncState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: string };

/**
 * 通用异步数据 hook。
 *
 * 关键约束：
 *  · 参数变化时**立即回到 loading**并丢弃旧数据 —— 否则切换指数会短暂
 *    显示上一个指数的行情（数据串号，比加载慢更危险）
 *  · 用自增 seq 防止竞态：慢请求晚返回不能覆盖新请求的结果
 *  · `enabled: false` 时不请求（用于「数据未就绪前不打扰上游」）
 *  · `pollMs` 定时重取：**必须配 keepPrevious**，否则每轮都会闪回
 *    「加载中」并把列表清空，看起来像页面在反复重载
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: readonly unknown[],
  opts: { enabled?: boolean; keepPrevious?: boolean; pollMs?: number } = {},
): AsyncState<T> & { reload: () => void } {
  const enabled = opts.enabled ?? true;
  const pollMs = opts.pollMs ?? 0;
  const [state, setState] = useState<AsyncState<T>>({
    status: 'loading', data: null, error: null,
  });
  const seq = useRef(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const my = ++seq.current;
    if (!opts.keepPrevious) setState({ status: 'loading', data: null, error: null });

    fn()
      .then((data) => {
        if (my !== seq.current) return;   // 竞态：丢弃过期响应
        setState({ status: 'ready', data, error: null });
      })
      .catch((e: unknown) => {
        if (my !== seq.current) return;
        const msg = e instanceof ApiError ? e.message : String(e);
        setState({ status: 'error', data: null, error: msg });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, tick]);

  /**
   * 定时重取。
   *
   * 两条约束：
   *  · 标签页在后台时不发请求 —— 用户看不见，纯耗流量与电量
   *  · 切回前台立刻补一次 —— 后台期间可能漏掉好几轮，
   *    若只等下一个周期会白等一整个间隔
   */
  useEffect(() => {
    if (!pollMs || !enabled) return;
    const id = setInterval(() => {
      if (!document.hidden) setTick((t) => t + 1);
    }, pollMs);
    const onVisible = () => {
      if (!document.hidden) setTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollMs, enabled]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/**
 * 服务健康度 + 状态栏数据。
 *
 * 心跳策略：在线时 30s 一次；离线时退避到 5s（避免服务未启动时刷爆日志与 CPU）。
 * 估值滞后（不可逆数据漏抓）会在状态栏显示红点 —— 这是需求 L3 的落地面。
 */
export function useHealth(): {
  health: HealthSummary | null;
  status: StatusData | null;
  online: boolean;
  reload: () => void;
} {
  const [health, setHealth] = useState<HealthSummary | null>(null);
  const [status, setStatus] = useState<StatusData | null>(null);
  const [online, setOnline] = useState(false);
  const [tick, setTick] = useState(0);
  const failRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        // 市场日历由 /api/status 的 lastTradingDay 提供，
        // /api/calendar/markets 只用于「该市场日历是否已建立」判断，故不需要其返回值
        const [h, , s] = await Promise.all([
          api.status(),
          api.calendarMarkets(),
          api.syncStats(),
        ]);
        if (cancelled) return;
        failRef.current = 0;
        setHealth(h);
        setOnline(true);
        setStatus({
          serverOnline: true,
          lastTradingDay: {
            cn: h.lastTradingDay.CN,
            hk: h.lastTradingDay.HK,
          },
          cacheTime: `${s.dbKb} KB`,
          // L3：估值滞后天数 >0 即表示不可逆数据正在流失
          valuationAlert: h.valuationLagDays > 0,
          warnings: h.gaps.length > 0
            ? [`${h.gaps.length} 处数据缺口：${h.gaps.slice(0, 3).map((g) => g.code).join('、')}`]
            : [],
        });
      } catch {
        if (cancelled) return;
        failRef.current += 1;
        setOnline(false);
        setStatus((prev) => ({ ...(prev ?? { lastTradingDay: {} }), serverOnline: false }) as StatusData);
      } finally {
        if (!cancelled) {
          const ms = failRef.current === 0 ? 30_000 : 5_000;
          timer = setTimeout(poll, ms);
        }
      }
    };

    void poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [tick]);

  return { health, status, online, reload: () => setTick((t) => t + 1) };
}
